import { useState, useRef, useEffect, useCallback, type RefObject } from 'react'
import { Button } from '../../Button'
import { GlassHoverEffect } from '../../GlassHoverEffect'
import { 
  HiOutlineBell, 
  HiOutlineArrowPath, 
  HiOutlineInbox,
  HiOutlineUserCircle,
  HiOutlineCheckCircle,
  HiOutlineClock
} from 'react-icons/hi2'
import { AnimatePresence, motion } from 'framer-motion'
import { cn, extractPlainText } from '../../../utils/formatter'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { demandaService, notificacaoService } from '../../../services'
import { toast } from '../../Toast'
import { useAuth } from '../../../contexts/AuthContext'
import { ROUTES } from '../../../routes/constants'
import type { Demanda, Notificacao } from '../../../models'

type UnifiedNotification = {
  id: string
  demandId: number
  title: string
  message: string
  createdAt: string
  read: boolean
  demanda?: Demanda
  notificacao: Notificacao
}

type NotificationFilter = 'all' | 'assigned' | 'solved' | 'inprogress'

const NotificationsMenu = () => {
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>('all')
  const menuRef = useRef<HTMLDivElement>(null)
  const fetchInProgressRef = useRef(false)
  const { user } = useAuth()
  const navigate = useNavigate()

  // Função auxiliar para verificar se demanda está atribuída ao usuário
  const isAssignedToMe = useCallback((demanda?: Demanda): boolean => {
    if (!demanda || !user?.id) return false
    
    // Verifica se está no array de atribuídas
    if (demanda.atribuidas && demanda.atribuidas.length > 0) {
      return demanda.atribuidas.some(
        atribuida => atribuida.usuario_fk === user.id && atribuida.active
      )
    }
    
    // Verifica se é o demandado direto
    if (demanda.demandado_fk === user.id) return true
    
    // Verifica no array de demandados
    if (demanda.demandados && demanda.demandados.length > 0) {
      return demanda.demandados.some(d => d.id === user.id)
    }
    
    return false
  }, [user?.id])

  // Filtrar notificações baseado na tab ativa
  const filteredNotifications = notifications.filter(notification => {
    if (activeFilter === 'all') return true
    
    // Filtros por característica da demanda
    if (activeFilter === 'assigned') {
      return isAssignedToMe(notification.demanda)
    }
    
    if (activeFilter === 'solved') {
      return notification.demanda?.status === 'SOLUCIONADA'
    }
    
    if (activeFilter === 'inprogress') {
      return notification.demanda?.status === 'EMPROGRESSO'
    }
    
    return false
  })

  const unreadCount = notifications.reduce((count, notification) => count + (notification.read ? 0 : 1), 0)
  const unreadAssignedCount = notifications.filter(n => !n.read && isAssignedToMe(n.demanda)).length
  const unreadSolvedCount = notifications.filter(n => !n.read && n.demanda?.status === 'SOLUCIONADA').length
  const unreadInProgressCount = notifications.filter(n => !n.read && n.demanda?.status === 'EMPROGRESSO').length

  const getTimeLabel = useCallback((date?: string) => {
    if (!date) {
      return 'Atualizado recentemente'
    }

    try {
      const parsed = new Date(date)
      if (Number.isNaN(parsed.getTime())) {
        return 'Atualizado recentemente'
      }
      return formatDistanceToNow(parsed, {
        addSuffix: true,
        locale: ptBR,
      })
    } catch {
      return 'Atualizado recentemente'
    }
  }, [])

  // Função para formatar mensagem HTML para texto plano
  const formatNotificationMessage = useCallback((message: string): string => {
    if (!message) return 'Nova atualização na demanda'
    
    // Extrair texto plano removendo tags HTML
    const plainText = extractPlainText(message)
    
    // Limitar tamanho para exibição na notificação
    if (plainText.length > 100) {
      return plainText.substring(0, 100) + '...'
    }
    
    return plainText || 'Nova atualização na demanda'
  }, [])

  const mapNotifications = useCallback((notificacoes: Notificacao[]): UnifiedNotification[] => {
    return notificacoes.map(notif => {
      const unified: UnifiedNotification = {
        id: `notif-${notif.id}`,
        demandId: notif.demanda_fk,
        title: notif.titulo,
        message: notif.comentario || 'Nova atualização na demanda',
        createdAt: notif.created_at,
        read: notif.visualizado,
        notificacao: notif,
      }
      
      if (notif.demanda) {
        unified.demanda = notif.demanda
      }
      
      return unified
    }).sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateB - dateA
    })
  }, [])

  const fetchNotifications = useCallback(async () => {
    if (fetchInProgressRef.current) {
      return
    }

    if (!user?.id) {
      setNotifications([])
      setIsLoading(false)
      return
    }

    fetchInProgressRef.current = true
    setIsLoading(true)

    try {
      const notificacoes = await notificacaoService.getNotificacoes()
      const mapped = mapNotifications(notificacoes)
      setNotifications(mapped)
    } catch (error) {
      console.error('Erro ao buscar notificações:', error)
      toast.error('Não foi possível carregar as notificações.')
    } finally {
      setIsLoading(false)
      fetchInProgressRef.current = false
    }
  }, [user?.id, mapNotifications])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications()
    }
  }, [showNotifications, fetchNotifications])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowNotifications(false)
      }
    }

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showNotifications])

  const getActiveIndex = () => {
    if (filteredNotifications.length === 0) return 0
    const firstUnreadIndex = filteredNotifications.findIndex((notification) => !notification.read)
    return firstUnreadIndex !== -1 ? firstUnreadIndex : 0
  }

  // Tabs de filtro
  const filterTabs: Array<{ value: NotificationFilter; label: string; count: number; icon: React.ReactNode }> = [
    { value: 'all', label: 'Todas', count: unreadCount, icon: <HiOutlineInbox className="w-4 h-4" /> },
    { value: 'assigned', label: 'Atribuídas', count: unreadAssignedCount, icon: <HiOutlineUserCircle className="w-4 h-4" /> },
    { value: 'solved', label: 'Solucionadas', count: unreadSolvedCount, icon: <HiOutlineCheckCircle className="w-4 h-4" /> },
    { value: 'inprogress', label: 'Em Progresso', count: unreadInProgressCount, icon: <HiOutlineClock className="w-4 h-4" /> },
  ]

  const handleViewDemanda = async (notification: UnifiedNotification) => {
    if (!notification.demandId) return
    
    // Marca notificação como visualizada no backend
    if (!notification.read) {
      try {
        await notificacaoService.marcarComoVisualizada(notification.notificacao.id)
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        )
      } catch (err) {
        console.error('Erro ao marcar notificação como visualizada:', err)
      }
    }
    
    setShowNotifications(false)
    navigate(ROUTES.DEMANDS.VIEW(String(notification.demandId), { type: 'all' }))
  }

  const handleViewAll = () => {
    setShowNotifications(false)
    navigate(ROUTES.NOTIFICATIONS)
  }

  const handleMarkAsRead = async (notification: UnifiedNotification) => {
    if (!notification.read) {
      try {
        await notificacaoService.marcarComoVisualizada(notification.notificacao.id)
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        )
        // toast.success('Notificação marcada como lida.')
      } catch (error) {
        console.error('Erro ao marcar notificação:', error)
        toast.error('Não foi possível marcar como lida.')
      }
    }
  }

  const handleRefuseObserver = async (notification: UnifiedNotification) => {
    try {
      // Primeiro marca a notificação como visualizada
      await notificacaoService.marcarComoVisualizada(notification.notificacao.id)
      
      // Remove o usuário como observador da demanda
      await demandaService.recusarObservador(notification.demandId)
      
      // Remove a notificação da lista
      setNotifications((prev) => prev.filter((item) => item.id !== notification.id))
      toast.success('Você não receberá mais atualizações desta demanda.')
      
      // Atualiza a lista de notificações
      fetchNotifications()
    } catch (error) {
      console.error('Erro ao recusar observador:', error)
      toast.error('Não foi possível recusar a observação. Tente novamente.')
    }
  }

  // Verifica se a notificação é sobre ser adicionado como observador
  const isObserverNotification = (notification: UnifiedNotification): boolean => {
    const title = notification.title.toLowerCase()
    // Apenas notificações sobre SER ADICIONADO como observador devem mostrar o botão recusar
    // Não deve mostrar para: "recusou ser observador", "removido como observador", etc.
    return title.includes('adicionou você como observador') || 
           (title.includes('adicionou') && title.includes('observador') && title.includes('você'))
  }

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowNotifications((prev) => !prev)}
        className="relative p-2 h-9 w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
      >
        <HiOutlineBell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              "absolute top-full mt-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl shadow-2xl z-[9999] overflow-hidden",
              // Mobile: centralizado na tela, largura quase total
              "fixed left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)]",
              // Tablet: posicionado à direita, largura média
              "sm:fixed sm:left-auto sm:translate-x-0 sm:right-4 sm:w-96",
              // Desktop: posicionado à direita do botão, largura generosa
              "md:absolute md:right-0 md:w-[28rem]",
              // Desktop grande: largura ainda maior para aproveitar espaço
              "lg:w-[32rem]"
            )}          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Notificações
                </span>

                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full">
                      {unreadCount} não lidas
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => fetchNotifications()}
                    disabled={isLoading}
                    className={cn(
                      'flex size-8 items-center justify-center rounded-md border border-transparent text-gray-500 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300',
                      isLoading && 'cursor-wait opacity-60'
                    )}
                    title="Atualizar notificações"
                  >
                    <HiOutlineArrowPath className={cn('h-4 w-4', isLoading && 'animate-spin')} />
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs de Filtro */}
            <div className="px-2 py-2 border-b border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
              <div className="flex flex-col gap-2 p-1.5 bg-gray-100 dark:bg-neutral-700/50 rounded-lg">
                {/* Primeira linha: 3 tabs */}
                <div className="grid grid-cols-3 gap-2">
                  {filterTabs.slice(0, 3).map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setActiveFilter(tab.value)}
                      className={cn(
                        'flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium rounded-md transition-all duration-200',
                        'hover:bg-gray-200 dark:hover:bg-neutral-600',
                        activeFilter === tab.value
                          ? 'bg-white dark:bg-neutral-800 text-blue-600 dark:text-blue-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      )}
                    >
                      {tab.icon}
                      <span className="truncate">{tab.label}</span>
                      {tab.count > 0 && (
                        <span className={cn(
                          'px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                          activeFilter === tab.value
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-200 dark:bg-neutral-600 text-gray-700 dark:text-gray-300'
                        )}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {/* Segunda linha: 2 tabs centralizadas */}
                <div className="grid grid-cols-2 gap-2">
                  {filterTabs.slice(3).map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setActiveFilter(tab.value)}
                      className={cn(
                        'flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium rounded-md transition-all duration-200',
                        'hover:bg-gray-200 dark:hover:bg-neutral-600',
                        activeFilter === tab.value
                          ? 'bg-white dark:bg-neutral-800 text-blue-600 dark:text-blue-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      )}
                    >
                      {tab.icon}
                      <span className="truncate">{tab.label}</span>
                      {tab.count > 0 && (
                        <span className={cn(
                          'px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                          activeFilter === tab.value
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-200 dark:bg-neutral-600 text-gray-700 dark:text-gray-300'
                        )}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto p-2">
              {isLoading ? (
                <div className="space-y-2 py-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={`notification-skeleton-${index}`}
                      className="h-20 rounded-lg bg-gray-100/80 dark:bg-neutral-700/60 animate-pulse"
                    />
                  ))}
                </div>
              ) : filteredNotifications.length > 0 ? (
                <GlassHoverEffect
                  activeIndex={getActiveIndex()}
                  direction="vertical"
                  className="flex flex-col gap-3 relative"
                  glassClassName="rounded-lg shadow-sm bg-blue-50/90 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-700/40 backdrop-blur-sm"
                >
                  {({ containerRef, containerProps, getItemProps }) => (
                    <div
                      ref={containerRef as RefObject<HTMLDivElement>}
                      className="flex flex-col gap-3 relative"
                      {...containerProps}
                    >
                      {filteredNotifications.map((notification, index) => {
                        const borderColor = notification.read 
                          ? 'border-transparent' 
                          : 'border-blue-500/30'
                        
                        return (
                          <div
                            key={notification.id}
                            {...getItemProps(index)}
                            className={cn(
                              'px-4 py-3 hover:bg-transparent transition-colors border-l-2 rounded-lg relative z-10',
                              notification.read
                                ? 'border-transparent opacity-70'
                                : borderColor
                            )}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                  {formatNotificationMessage(notification.message)}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5">
                                  {getTimeLabel(notification.createdAt)}
                                </p>
                              </div>
                              {!notification.read && (
                                <span className="inline-flex h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                              )}
                            </div>

                            <div className="mt-2.5 flex items-center gap-3 text-xs">
                              <button
                                type="button"
                                onClick={() => handleViewDemanda(notification)}
                                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                              >
                                Ver demanda
                              </button>
                              
                              {!notification.read && (
                                <>
                                  <span className="text-gray-300 dark:text-gray-600">•</span>
                                  <button
                                    type="button"
                                    onClick={() => handleMarkAsRead(notification)}
                                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                  >
                                    Marcar como lida
                                  </button>
                                </>
                              )}
                              
                              {isObserverNotification(notification) && (
                                <>
                                  <span className="text-gray-300 dark:text-gray-600">•</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRefuseObserver(notification)}
                                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                  >
                                    Recusar
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </GlassHoverEffect>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {!user 
                    ? 'Faça login para visualizar suas notificações.'
                    : notifications.length === 0
                      ? 'Nenhuma notificação no momento.'
                      : activeFilter === 'assigned'
                        ? 'Nenhuma demanda atribuída a você no momento.'
                        : activeFilter === 'solved'
                          ? 'Nenhuma demanda solucionada no momento.'
                          : activeFilter === 'inprogress'
                            ? 'Nenhuma demanda em progresso no momento.'
                            : 'Nenhuma notificação nesta categoria.'
                  }
                </div>
              )}
            </div>

            {filteredNotifications.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700">
                <button
                  type="button"
                  onClick={handleViewAll}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  Ver todas as notificações
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export { NotificationsMenu }